import React, { useCallback, useState } from 'react';

import { Box, Divider, Text } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { GIFT_CARD_NUMBER_TRANSLATION_ID } from 'components/payment-method/constants';
import { toaster } from 'components/toaster/toaster';
import { usePrepaidByNumber } from 'hooks/prepaid/use-prepaid-by-number';
import useErrorModal from 'hooks/use-error-modal';
import { AddGiftCardInput } from 'pages/cart/payment/add-gift-card-input';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useUIContext } from 'state/ui';
import logger from 'utils/logger';

import GiftCardImage from '../gift-card-image';
import { useCardNumber } from '../use-giftcard-number';

export const TransferGiftCardModal: React.FC = () => {
  const { getPrepaidPaymentMethod, mergePrepaidCardBalances } = usePaymentContext();
  const { formatCurrencyForUser } = useUIContext();
  const router = useRouter();

  const [transferInfo, setTransferInfo] = useState<{
    transferCardBalance?: number;
    cardNumber?: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { giftCardNumber, errors, isValidGiftCardNumber, handleCardNumberChange } = useCardNumber();
  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });
  const { getPrepaidByNumber } = usePrepaidByNumber();

  const showGiftCardAddingErrorModal = useCallback(
    (error: unknown) => {
      // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
      openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
      logger.error({ error, message: 'Error transfer gift card' });
    },
    [formatMessage, openErrorDialog]
  );

  const transferCard = useCallback(
    async (event: any) => {
      event.preventDefault();
      setIsLoading(true);
      try {
        const { data, errors } = await getPrepaidByNumber({ cardNumber: giftCardNumber });
        if (!data) {
          showGiftCardAddingErrorModal(errors);
        } else {
          const transferCardBalance = data?.balance;
          setTransferInfo({ transferCardBalance, cardNumber: giftCardNumber });
        }
      } catch (error) {
        showGiftCardAddingErrorModal(error);
      } finally {
        setIsLoading(false);
      }
    },
    [getPrepaidByNumber, giftCardNumber, showGiftCardAddingErrorModal]
  );

  const confirmTransferCard = async () => {
    setIsLoading(true);
    try {
      const newBalance = await mergePrepaidCardBalances({
        sourceCardNumber: cardNumber || '',
      });

      logEvent(CustomEventNames.GIFT_CARD_BALANCE_TRANSFER, EventTypes.Other, {
        'Current Balance': newBalance,
      });

      toaster.positive(formatMessage({ id: 'transferBalanceCompletedMessage' }));

      router.dismiss();
    } catch (error) {
      // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
      openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
      logger.error({ error, message: 'Error transfer gift card' });
    } finally {
      setIsLoading(false);
    }
  };

  // part 1 - enter info
  if (!transferInfo) {
    return (
      <CardModal
        headerText={formatMessage(
          { id: 'transferBalanceFromPrepaidCard' },
          { cardAlias: formatMessage({ id: 'cardAlias.bk' }) }
        )}
        testID="transfer-giftcard-form"
        analyticsEvent="Transfer gift card"
        standardPadding
        footer={
          <ActionButton
            fullWidth
            testID="transfer-now"
            disabled={!isValidGiftCardNumber}
            onPress={transferCard}
            isLoading={isLoading}
          >
            {formatMessage({ id: 'transfer' })}
          </ActionButton>
        }
      >
        <GiftCardImage />
        <AddGiftCardInput
          onChange={handleCardNumberChange}
          errorMessage={errors?.giftCardNumber}
          label={formatMessage({ id: GIFT_CARD_NUMBER_TRANSLATION_ID })}
          testID="transfer-giftcard-input"
        />
        <ErrorDialog />
      </CardModal>
    );
  }

  // part 2 - confirm (has transferInfo filled in)
  const { transferCardBalance, cardNumber } = transferInfo;

  const currentBalance = getPrepaidPaymentMethod()?.prepaid?.currentBalance;
  const formattedCurrecyBalance = formatCurrencyForUser(currentBalance);
  const formattedTransferCardBalance = formatCurrencyForUser(transferCardBalance);
  const formatCardNumber = cardNumber?.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ');

  const cardAlias = formatMessage({ id: 'cardAlias.bk' });
  const isTransferCardBalanceValid = transferCardBalance && transferCardBalance > 0;
  const { actionDetails, actionHandler, actionText } = isTransferCardBalanceValid
    ? {
        actionDetails: formatMessage(
          { id: 'wouldYouLikeToAddToPrepaidCard' },
          { amount: formattedTransferCardBalance, cardAlias }
        ),
        actionText: formatMessage({ id: 'yesTransfer' }, { amount: formattedTransferCardBalance }),
        actionHandler: confirmTransferCard,
      }
    : {
        actionDetails: formatMessage({ id: 'wouldYouLikeToAddADifferentCard' }, { cardAlias }),
        actionText: formatMessage({ id: 'yesAddNewCard' }, { cardAlias }),
        actionHandler: () => {
          //navigate(-1) go back to transfer-gift-card route
          setTransferInfo(null);
        },
      };

  return (
    <CardModal
      scrollView
      standardPadding
      analyticsEvent="Success transfer gift card"
      headerText={formatMessage(
        { id: 'prepaidCardBalance' },
        { cardAlias: formatMessage({ id: 'cardAlias.bk' }) }
      )}
      footer={
        <ActionButton fullWidth testID="confirm-now" onPress={actionHandler} isLoading={isLoading}>
          {actionText}
        </ActionButton>
      }
    >
      <GiftCardImage />
      <Text testID="card-balance">
        {formatMessage(
          {
            id: 'theCardBalanceIs',
          },
          {
            cardNumber: <Text fontWeight="bold">{formatCardNumber}</Text>,
          }
        )}
      </Text>
      <CurrentBalanceText testID="transfer-balance">
        {formattedTransferCardBalance}
      </CurrentBalanceText>
      <ModalHr />
      <CurrentBalanceDescription>
        {formatMessage({ id: 'yourBalanceIs' }, { currentBalance: formattedCurrecyBalance })}
      </CurrentBalanceDescription>
      <Text>{actionDetails}</Text>
    </CardModal>
  );
};

export const CurrentBalanceText = Text.withConfig({
  fontSize: 'lg',
  marginTop: spacing.$2,
  fontWeight: 'bold',
});

export const ModalHr = Divider.withConfig({
  width: '100%',
  thickness: '1',
  marginX: 0,
  marginY: '$8',
});

export const CurrentBalanceDescription = Box.withConfig({
  _text: {
    fontSize: 'sm',
    fontWeight: 'bold',
  },
});
